import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import type { Client } from '../../api/client.js';
import { COULEURS, POLICE_SYSTEME } from '../../app/styles.js';
import { DesignationDetaillee } from './devis/Designation.js';
import type { Designation } from './devis/source.js';
import { dateFr } from './modele.js';

/**
 * Portail héritier (lignes détaillées, 30/10/2026) : ce que couvrent les devis envoyés du dossier et mes factures, avec la même
 * désignation détaillée que l'équipe (route `portail.prestations`). Aucun montant n'est lu ni affiché ; police système, 18 px au
 * moins (UX-22).
 */
export interface PrestationsPortail {
  devis: { id: string; status: 'envoye' | 'signe'; lines: Designation[] }[];
  factures: { number: string; issuedAt: string; lines: Designation[] }[];
}

export const chargerPrestationsClient = (client: Client): Promise<PrestationsPortail> =>
  client.appeler<PrestationsPortail>('portail.prestations');

const styleRacine: CSSProperties = { fontFamily: POLICE_SYSTEME, fontSize: 18, lineHeight: 1.5, color: COULEURS.marineFonce };

export function PrestationsClient({ charger }: { charger: () => Promise<PrestationsPortail> }): ReactElement {
  const [vue, setVue] = useState<PrestationsPortail | null>(null);
  const [erreur, setErreur] = useState(false);
  useEffect(() => {
    let actif = true;
    charger()
      .then((v) => actif && setVue(v))
      .catch(() => actif && setErreur(true));
    return () => {
      actif = false;
    };
  }, [charger]);
  const vide = vue !== null && vue.devis.length === 0 && vue.factures.length === 0;
  return (
    <section data-composant="prestations-client" style={styleRacine} aria-labelledby="titre-prestations-client">
      <h2 id="titre-prestations-client" style={{ fontSize: 24 }}>
        Ce que couvre l’accompagnement
      </h2>
      {vue === null && !erreur ? <p role="status">Chargement en cours…</p> : null}
      {erreur ? <p role="alert">Le détail de l’accompagnement n’a pas pu être chargé. Réessayez plus tard. Cabinet MAAT</p> : null}
      {vide ? <p>Aucun devis ni aucune facture ne vous a encore été adressé.</p> : null}
      {vue?.devis.map((d) => (
        <article key={d.id}>
          <h3 style={{ fontSize: 20 }}>{d.status === 'signe' ? 'Devis signé' : 'Devis envoyé'}</h3>
          {d.lines.map((l, i) => (
            <DesignationDetaillee key={i} designation={l} />
          ))}
        </article>
      ))}
      {vue?.factures.map((f) => (
        <article key={f.number}>
          <h3 style={{ fontSize: 20 }}>
            Facture {f.number} du {dateFr(f.issuedAt)}
          </h3>
          {f.lines.map((l, i) => (
            <DesignationDetaillee key={i} designation={l} />
          ))}
        </article>
      ))}
    </section>
  );
}
